import React, { FC, KeyboardEvent, useMemo } from 'react';

import styled from 'styled-components';

import { UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EUIFormItemVariant, UIFormItem } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UISelectPicker, { EUISelectPickerVariant } from '@aquariuxfintech/aqx-trader-react-ui/select-picker';
import UIText, { EUITextVariant } from '@aquariuxfintech/aqx-trader-react-ui/text';

import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { ISignUpDemoValues } from '@aqx-core/common/hooks/form/use-sign-up-demo-form';
import { useSignUpDemo } from '@aqx-core/common/services/sign-up-demo';

interface IPhoneNumberFieldProps {
  dialCode: string;
  localPhoneNumber: string;
  register: UseFormRegister<ISignUpDemoValues>;
  setValue: UseFormSetValue<ISignUpDemoValues>;
  trigger: UseFormTrigger<ISignUpDemoValues>;
  handleKeyDown: (event: KeyboardEvent) => void;
  dialCodeError: string | undefined;
  localPhoneNumberError: string | undefined;
}

const PhoneNumberField: FC<IPhoneNumberFieldProps> = ({
  dialCode,
  localPhoneNumber,
  register,
  trigger,
  setValue,
  dialCodeError,
  localPhoneNumberError,
  handleKeyDown
}) => {
  const { t } = useTranslation();
  const { countries } = useSignUpDemo();

  const countryOptions = useMemo(() => {
    return countries.map(({ dialCode, country }) => ({
      label: `${country} (${dialCode})`,
      value: dialCode
    }));
  }, [countries]);

  // TODO: Do proper a select input component - Denlie
  // If select a dial code which as multiple , e.g. +61 for Australia, others with same value e.g. Christmas Island and Cocoas Island is selected also. Existing issue in 2.0.
  // Border radius for when the 2 inputs intersect needs to be 0px
  // Searchable dial code
  return (
    <StyledContainer>
      <UIText typography={ETypography.TEXT_L_REGULAR}>{t('Phone Number')}</UIText>
      <StyledInputSelectContainer>
        <StyledSelectorContainer>
          <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} size="large">
            <UISelectPicker<string>
              variant={EUISelectPickerVariant.SEARCHABLE_SELECTOR}
              onSelect={(val) => {
                setValue('dialCode', val);
                trigger('dialCode');
              }}
              placeholder="+XX"
              options={countryOptions}
              selectedValue={dialCode}
              error={dialCodeError && ' '} // To show error borders
              dataTestId={EDataTestId.SIGN_UP_COUNTRY_SELECTOR}
              size="large"
              dropdownWidth="240px"
              dropDownMaxHeight={200}
              valueLabel={dialCode}
              useLabelToSearch
              searchPlaceholder={t('Search')}
            />
          </UIFormItem>
        </StyledSelectorContainer>
        <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} size="large">
          <UIInput
            {...register('localPhoneNumber')}
            variant={EUIInputVariant.BORDERED_TEXT}
            placeholder={t('Enter your phone number')}
            value={localPhoneNumber}
            error={localPhoneNumberError && ' '}
            onKeyDown={handleKeyDown}
            dataTestId={EDataTestId.DEMO_SIGN_UP_NAME_INPUT}
            size="large"
          />
        </UIFormItem>
      </StyledInputSelectContainer>
      {!!(dialCodeError || localPhoneNumberError) && (
        <UIText variant={EUITextVariant.ERROR} error={dialCodeError || localPhoneNumberError} />
      )}
    </StyledContainer>
  );
};

export default PhoneNumberField;
const StyledContainer = styled.div``;

const StyledInputSelectContainer = styled.div`
  display: flex;
  margin-top: ${ESpacing.XXXS}px;
`;

const StyledSelectorContainer = styled.div`
  width: 92px;
`;
